import bulletDot from '@/assets/figma/login-bullet.svg';
import logoDot from '@/assets/figma/login-logo-dot.svg';
import type { SocialProvider } from '@/entities/auth/api/auth.api';
import { SOCIAL_PROVIDER_LABEL } from '@/entities/auth/social/socialRedirect';
import { LoginForm } from '@/pages/login/components/LoginForm';
import { SocialLoginButtons } from '@/pages/login/components/SocialLoginButtons';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { toastApi } from '@/shared/utils/toast';
import { getRouteApi } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';

const route = getRouteApi('/login');

/** 브랜드 패널의 앱 전용 기능 목록 (M-01) */
const APP_ONLY_LINES = [
  '회원가입 · 비밀번호 재설정',
  '사업자 정보 등록',
  '거래처 · 필터링 신청',
  '판촉영업 위탁 계약 전자서명',
  '처방 분석 · 맥스매치',
];

/** M-03 X1 · X4 — SNS 로그인 실패 팝업 */
type SocialNotice =
  { kind: 'failed'; provider?: SocialProvider } | { kind: 'signup' };

const socialNoticeText = (notice: SocialNotice) =>
  notice.kind === 'signup'
    ? {
        title: '회원가입은 앱에서 할 수 있어요',
        body: '루다링크 앱에서 가입한 뒤 같은 계정으로 로그인해주세요.',
      }
    : {
        title: '로그인할 수 없어요',
        body: `${notice.provider ? `${SOCIAL_PROVIDER_LABEL[notice.provider]} ` : ''}로그인이 취소됐거나 연결에 실패했어요. 잠시 후 다시 시도해주세요.`,
      };

/**
 * M-01 로그인 — 로그아웃 상태의 유일한 진입점.
 * 데스크톱은 좌 브랜드(600) / 우 폼(400), 모바일(<1024)은 폼만 한 단으로 둔다.
 */
export const LoginPage = () => {
  // SNS 인증 진행 중에는 다른 버튼과 이메일 폼을 비활성화한다
  const [pendingProvider, setPendingProvider] = useState<string | null>(null);
  const [socialNotice, setSocialNotice] = useState<SocialNotice | null>(null);

  const search = route.useSearch();
  const navigate = route.useNavigate();

  useEffect(() => {
    // 새로고침 때 다시 뜨지 않도록 처리한 reason은 주소에서 지운다
    switch (search.reason) {
      case 'expired':
        // X7 — 세션 만료로 들어왔을 때 상단 토스트 3초
        toastApi.info('세션이 만료되었어요. 다시 로그인해주세요.', 3);
        break;
      case 'social_failed':
        // X1 — SNS 인증 취소 · 실패 (콜백 화면에서 돌아옴). 비활성 계정도 같은 문구를 쓴다
        setSocialNotice({ kind: 'failed', provider: search.provider });
        break;
      case 'signup_required':
        // X4 — 앱에서 해당 SNS로 가입 · 연결한 이력이 없음
        setSocialNotice({ kind: 'signup' });
        break;
      default:
        return;
    }
    void navigate({
      search: (prev) => ({ ...prev, reason: undefined, provider: undefined }),
      replace: true,
    });
  }, [search.reason, search.provider, navigate]);

  const notice = socialNotice && socialNoticeText(socialNotice);

  return (
    <div className="flex min-h-dvh bg-surface">
      <aside className="hidden w-[600px] shrink-0 flex-col bg-ink px-[72px] py-20 lg:flex">
        <div className="flex items-center gap-2">
          <img src={logoDot} alt="" width={22} height={22} />
          <span className="text-[22px] leading-[33px] font-bold text-white">
            RUDALINK
          </span>
        </div>
        <h2 className="mt-10 text-[34px] leading-[48px] font-bold text-white">
          처방 등록과 정산 실적을
          <br />웹 화면에서 바로 확인하세요
        </h2>
        <p className="mt-5 text-[15px] leading-[26px] text-[#9a9a9a]">
          앱의 기능 중 거래처 · 처방 등록 · 실적 · 의약품 검색을 큰 화면에서
          다룰 수 있습니다.
        </p>
        <div className="mt-12 flex flex-col gap-3 rounded-xl bg-[#1c1c1c] px-6 py-[22px]">
          <div className="flex items-start gap-2">
            <span className="shrink-0 rounded-badge bg-lime px-2 py-[3px] text-[11px] leading-[17px] font-medium whitespace-nowrap text-ink">
              웹 미지원
            </span>
            <span className="text-sm leading-[21px] font-medium text-white">
              아래 기능은 웹에서 제공하지 않습니다 — 앱에서 이용해 주세요
            </span>
          </div>
          {APP_ONLY_LINES.map((line) => (
            <div key={line} className="flex items-center gap-2.5">
              <img src={bulletDot} alt="" width={4} height={13} />
              <span className="text-[13px] leading-5 text-[#b5b5b5]">
                {line}
              </span>
            </div>
          ))}
        </div>
      </aside>

      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="flex w-full max-w-[400px] flex-col">
          {/* 모바일(<1024): 브랜드 패널 대신 로고만 위에 둔다 (README §8) */}
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <img src={logoDot} alt="" width={18} height={18} />
            <span className="text-lg leading-7 font-bold text-ink">
              RUDALINK
            </span>
          </div>
          <h1 className="text-[28px] leading-[42px] font-bold text-ink">
            로그인
          </h1>
          <p className="mt-2 text-sm leading-[21px] text-ink-4">
            루다링크 계정으로 로그인하세요.
          </p>

          <SocialLoginButtons
            className="mt-8"
            pendingProvider={pendingProvider}
            onPendingChange={setPendingProvider}
            onError={(provider) =>
              setSocialNotice({ kind: 'failed', provider })
            }
            redirectTo={search.redirect}
          />

          <div className="my-7 flex items-center gap-3">
            <span className="h-px flex-1 bg-[#e5e5e5]" />
            <span className="text-xs leading-[18px] text-ink-7">
              또는 이메일로 로그인
            </span>
            <span className="h-px flex-1 bg-[#e5e5e5]" />
          </div>

          <LoginForm
            disabled={pendingProvider !== null}
            redirectTo={search.redirect}
          />

          {/* 모바일: 앱 전용 기능 안내를 폼 아래에 접어서 둔다 (README §8) */}
          <details className="group mt-8 rounded-xl bg-field px-4 py-3.5 lg:hidden">
            <summary className="flex cursor-pointer list-none items-center gap-2 text-[13px] leading-5 font-medium text-ink-2">
              <span className="rounded-badge bg-lime px-2 py-[3px] text-[11px] leading-[17px] text-ink">
                웹 미지원
              </span>
              웹에서 제공하지 않는 기능
              <ChevronDown className="ml-auto size-4 text-ink-6 transition-transform group-open:rotate-180" />
            </summary>
            <ul className="mt-3 flex flex-col gap-1.5 text-[13px] leading-5 text-ink-4">
              {APP_ONLY_LINES.map((line) => (
                <li key={line}>· {line}</li>
              ))}
            </ul>
            <p className="mt-3 text-xs leading-[18px] text-ink-6">
              앱에서 이용해 주세요.
            </p>
          </details>
        </div>
      </main>

      <AlertDialog
        open={notice !== null}
        onOpenChange={(o) => !o && setSocialNotice(null)}
      >
        <AlertDialogContent>
          {notice && (
            <>
              <AlertDialogTitle>{notice.title}</AlertDialogTitle>
              <AlertDialogDescription>{notice.body}</AlertDialogDescription>
            </>
          )}
          <AlertDialogFooter>
            <AlertDialogAction>확인</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
